import { Map } from "immutable";
import isNil from "lodash/isNil";
import * as colors from "@material-ui/core/colors";
import { createTheme } from "@material-ui/core/styles";

import { defaultOptions as defaultThemeOptions, THEMES } from "altus-ui-theme";

import { Environments } from "app/app.constants";
import { WORK_SCHEDULE_OVERRIDE_REASONS } from "features/planner/planner.constants";

const defaultThemeType = THEMES.LIGHT;

const defaultTheme = createTheme({
  palette: {
    type: defaultThemeType,
  },
});

const personnelPlannerOptions = {
  typography: {
    useNextVariants: true,
  },
  palette: {
    environment: {
      [Environments.LOCAL]: colors.lightGreen[500],
      [Environments.DEV]: colors.orange[500],
      [Environments.TST]: colors.yellow[500],
      [Environments.SBX]: colors.purple[500],
      [Environments.SCA]: colors.red[500],
    },
    primary: {
      main: colors.blueGrey[700],
    },
    secondary: {
      main: defaultTheme.palette.common.white,
    },
  },
  zIndex: {
    calendarDay: 500,
    calendarWeek: 500,
    availabilityOverride: 600,
  },
  altus: {
    color: {
      accent: colors.deepOrange[700],
    },
    viewGroup: {
      width: 320,
    },
    background: {
      header: colors.blueGrey[700],
      headerChip: "#000a12",
      headerDark: colors.blueGrey[900],
      view: colors.grey[300],
      row: defaultTheme.palette.common.white,
      projectRow: colors.blueGrey[700],
    },
    largeRow: {
      height: 50,
    },
    dashboard: {
      tile: {
        height: 185,
      },
    },
    workScheduleReport: {
      defaultBorder: `1px solid ${colors.grey[400]}`,
      placeholderBorder: `1px solid transparent`,
      cellSize: 40,
    },
    calendar: {
      selectedColor: `${colors.amber[500]}`,
      contrastColor: `${colors.deepOrange[700]}`,
      workdayOffColor: {
        main: `${colors.grey[300]}`,
        hover: `${colors.lightBlue[100]}`,
      },
      [WORK_SCHEDULE_OVERRIDE_REASONS.SICK]: {
        main: `${colors.red[500]}ca`,
        hover: `${colors.red[500]}90`,
      },
      [WORK_SCHEDULE_OVERRIDE_REASONS.MILITARY_SERVICE]: {
        main: `${colors.green[700]}ca`,
        hover: `${colors.green[700]}90`,
      },
      [WORK_SCHEDULE_OVERRIDE_REASONS.TRAINING]: {
        main: `${colors.lime[600]}ca`,
        hover: `${colors.lime[600]}90`,
      },
      [WORK_SCHEDULE_OVERRIDE_REASONS.LEAVE_OF_ABSCENCE]: {
        main: `${colors.grey[700]}ca`,
        hover: `${colors.grey[700]}90`,
      },
      [WORK_SCHEDULE_OVERRIDE_REASONS.PROJECT_BASE]: {
        main: `${colors.grey[500]}ca`,
        hover: `${colors.grey[500]}90`,
      },
      [WORK_SCHEDULE_OVERRIDE_REASONS.VACATION]: {
        main: `${colors.grey[700]}ca`,
        hover: `${colors.grey[700]}90`,
      },
      [WORK_SCHEDULE_OVERRIDE_REASONS.LABOR_UNION]: {
        main: `${colors.indigo[500]}ca`,
        hover: `${colors.indigo[500]}90`,
      },
      [WORK_SCHEDULE_OVERRIDE_REASONS.QUARANTINE]: {
        main: `${colors.orange[500]}ca`,
        hover: `${colors.orange[500]}90`,
      },
      [WORK_SCHEDULE_OVERRIDE_REASONS.LAYOFF]: {
        main: `${colors.red[500]}ca`,
        hover: `${colors.red[500]}90`,
      },
      [WORK_SCHEDULE_OVERRIDE_REASONS.AVAILABLE]: {
        main: `${colors.blue[300]}70`,
        hover: `${colors.blue[500]}50`,
      },
      deploymentColor: {
        main: `${colors.blue[400]}ca`,
        hover: `${colors.blue[400]}90`,
      },
    },
  },
  overrides: {
    MuiTooltip: {
      tooltip: {
        backgroundColor: colors.grey[600],
      },
    },
    MuiAppBar: {
      colorDefault: {
        backgroundColor: colors.blueGrey[700],
        color: defaultTheme.palette.common.white,
      },
    },
    MuiTypography: {
      colorTextSecondary: {
        color: defaultTheme.palette.common.white,
      },
    },
    MuiListItemText: {
      root: {
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    MuiInputBase: {
      input: {
        padding: "6px 0 9px",
      },
    },
    MuiInput: {
      underline: {
        "&:after": {
          borderBottom: "2px solid #e34e25",
        },
        "&:hover:not(.Mui-disabled):before": {
          borderBottom: "2px solid #e34e25",
        },
      },
    },
    MuiFilledInput: {
      input: {
        padding: "26px 12px 5px",
      },
    },
  },
  props: {
    MuiSelect: {
      displayEmpty: true,
    },
    MuiTextField: {
      variant: "standard",
    },
  },
  mixins: {
    workScheduleOverride: reason => {
      if (isNil(reason)) return;

      return {
        background: personnelPlannerOptions.altus.calendar[reason].main,
        "&:hover": {
          background: personnelPlannerOptions.altus.calendar[reason].hover,
        },
      };
    },
  },
};

const mergedOptions = Map(defaultThemeOptions)
  .mergeDeep(Map(personnelPlannerOptions))
  .toJS();

export default createTheme(mergedOptions);
